'use client'

/**
 * TodaySubmittedColumn — R103.33 + R103.54 (2026-06-05)
 *
 * Schmale vertikale Spalte „Heute eingereicht" — sitzt auf /wertung rechts
 * neben der Ueberschrift „FGL Wertung", reicht bis zum vertikalen Rand der
 * Wertungs-Breite darunter (max-w-6xl).
 *
 * R103.54 Bugfix:
 *  - Edge-Auto-Scroll entfernt (blockte Klicks: 22% Top/Bottom-Zone triggerte
 *    Scroll waehrend der User die Zeile anklicken wollte → Zeile rutschte weg).
 *    Thermikhebel-Slider rechts uebernimmt das Scrollen komplett.
 *  - Alle Text-Farben theme-aware via var(--text) + var(--text-muted) statt
 *    hardcoded cirrus-Werte (waren in Light-Themes unsichtbar).
 *  - Links zeigen jetzt zu /wertung (statt /flight/[id], das ausgeloggte
 *    auf Lock-Screen wirft — Stephan-Prinzip aus R103.46 / Newsticker).
 */

import { useEffect, useRef, useState } from 'react'
import { Volume2, VolumeX, ChevronDown, ChevronUp } from 'lucide-react'
import Link from 'next/link'
import { useTranslation } from '@/i18n/client'

// R106.4.6 (Stephan-Spec 2026-06-08): Mobile-Akkordeon — Default eingeklappt.
const KEY_MOBILE_TODAY_COLLAPSED = 'fgl_today_col_mobile_collapsed'

interface TodayItem {
  id: string
  wertungsDistanceKm: number | null
  pilot: {
    id: string
    name: string
    slug: string | null
    avatarUrl: string | null
  }
}

function getInitial(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0]?.[0]?.toUpperCase() || '?'
}

export function TodaySubmittedColumn() {
  const { t } = useTranslation()
  const [items, setItems] = useState<TodayItem[]>([])
  const [total, setTotal] = useState(0)
  const [loaded, setLoaded] = useState(false)

  // r19.55.86 (Stephan-Spec 2026-08-05): Pixel-genaue Live-Messung der Sidebar-
  // Hoehe. Widget-Unterkante soll exakt auf Bottom-Edge der View-Tab-Pillen
  // (#wertung-view-tabs-row) sitzen. Fallback = 260 px. Responsive via
  // ResizeObserver + window resize.
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<number>(260)

  // R106.4.6 (Stephan-Spec 2026-06-08): Mobile-Akkordeon mit Klick auf den Header.
  // R107.8 (Stephan-Spec 2026-06-09): Default-Sichtbar ausgeklappt (vorher
  // eingeklappt). Persistiert weiter in localStorage — User-Wahl bleibt.
  const [mobileCollapsed, setMobileCollapsed] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY_MOBILE_TODAY_COLLAPSED)
      setMobileCollapsed(v === null ? false : v === '1')
    } catch { /* noop */ }
    setHydrated(true)
  }, [])
  const toggleMobile = () => {
    const next = !mobileCollapsed
    setMobileCollapsed(next)
    try { localStorage.setItem(KEY_MOBILE_TODAY_COLLAPSED, next ? '1' : '0') } catch { /* noop */ }
  }

  // R103.54: Edge-Auto-Scroll RAUS (blockierte Klicks auf Top/Bottom-Items).
  // Native wheel-Handler ({ passive: false }) + Thermikhebel-Slider reichen.
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const autoSpeedRef = useRef<number>(0)
  const rafIdRef = useRef<number | null>(null)

  // Auto-Scroll-RAF nur fuer den Thermikhebel — laeuft nur wenn speed != 0.
  useEffect(() => {
    const tick = () => {
      const el = scrollRef.current
      const speed = autoSpeedRef.current
      if (el && speed !== 0) {
        el.scrollTop += speed
      }
      rafIdRef.current = requestAnimationFrame(tick)
    }
    rafIdRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  // R103.53: React's synthetic onWheel ist PASSIVE — preventDefault wirkt nicht.
  // Nativer addEventListener mit { passive: false } via useEffect.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      el.scrollTop += e.deltaY
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [loaded])

  // r19.55.86 (Stephan-Spec 2026-08-05): Sidebar-Hoehe pixel-genau auf die
  // Unterkante der View-Tab-Pillen. Misst per getBoundingClientRect die Bottom
  // von #wertung-view-tabs-row und den Top der Sidebar-Card. Re-misst bei
  // Window-Resize + DOM-Aenderungen (ResizeObserver auf beide Elemente).
  useEffect(() => {
    if (typeof window === 'undefined') return
    let raf = 0
    const measure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const card = cardRef.current
        const pills = document.getElementById('wertung-view-tabs-row')
        if (!card || !pills) return
        const cardRect = card.getBoundingClientRect()
        const pillsRect = pills.getBoundingClientRect()
        const target = Math.max(120, Math.round(pillsRect.bottom - cardRect.top))
        setDynamicMaxHeight(target)
      })
    }
    measure()
    window.addEventListener('resize', measure)
    // ResizeObserver auf beide Elemente falls sich Pill-Row-Hoehe aendert
    // (z.B. Zeilenumbruch der Pillen bei mittlerer Viewport-Breite).
    const ro = new ResizeObserver(measure)
    const pillsEl = document.getElementById('wertung-view-tabs-row')
    if (pillsEl) ro.observe(pillsEl)
    if (cardRef.current) ro.observe(cardRef.current)
    // Nach kurzer Verzoegerung nochmal messen (Fonts + Reveal-Animation).
    const t1 = setTimeout(measure, 100)
    const t2 = setTimeout(measure, 400)
    const t3 = setTimeout(measure, 1200)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
      ro.disconnect()
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3)
    }
  }, [loaded])

  // R103.53 Thermikhebel-Slider — vertikaler Drag-Hebel an der rechten Seite.
  // Mitte = kein Scroll. Hoch ziehen = scroll-up mit Speed prop. zur Distanz.
  // Runter ziehen = scroll-down. Loslassen → Hebel federt zurueck in Mitte.
  const [throttlePos, setThrottlePos] = useState(50) // 0-100, 50 = neutral
  const [throttleActive, setThrottleActive] = useState(false)
  const throttleTrackRef = useRef<HTMLDivElement | null>(null)

  // R106.3 (Stephan-Spec 2026-06-07): Sound default-an, Toggle in Header.
  // Persistiert in localStorage damit User-Pref Session-uebergreifend bleibt.
  const [soundOn, setSoundOn] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem('fgl_vario_sound') !== 'off'
  })
  const soundOnRef = useRef(soundOn)
  useEffect(() => {
    soundOnRef.current = soundOn
    if (typeof window !== 'undefined') {
      localStorage.setItem('fgl_vario_sound', soundOn ? 'on' : 'off')
    }
  }, [soundOn])

  // R106.3 (Stephan-Wunsch 2026-06-07): Vario-Sound am Thermikhebel.
  // WINTER-Profil (mechanisch) 1:1-Port aus VarioToneGenerator.WINTER:
  //   - climbBaseFreq=440 Hz, climbMaxFreq=1100 Hz, LINEAR (kein log)
  //   - beepOn 120..500 ms, beepOff 100..400 ms (gemaechlich, mechanisch)
  //   - sinkBaseFreq=300 Hz, sinkMinFreq=100 Hz, continuous
  //   - climbThreshold 0.2 m/s, sinkThreshold -0.5 m/s
  //   - fadeMs 15 (lange Fade fuer weichen mechanischen Klang)
  //   - waveform ROUNDED (Sine + LowPass simuliert Winter-Saettigung)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const oscRef = useRef<OscillatorNode | null>(null)
  const gainRef = useRef<GainNode | null>(null)
  const audioRafRef = useRef<number | null>(null)
  // Beep-State fuer Steigen (Piepton + Pause)
  const beepStateRef = useRef<{ inBeep: boolean; switchAtMs: number }>({ inBeep: false, switchAtMs: 0 })
  // R106.4 (Stephan-Spec 2026-06-08): Vario-Wert fuer Sound ist an den
  // Schiebeweg gekoppelt, NICHT an den Scroll-Speed. Vollausschlag oben =
  // max Steig-Sound (+5 m/s), Mitte = Totband, voll unten = max Sink-Sound
  // (-5 m/s). Linear ueber den gesamten Weg, kein Sound-Plateau.
  const audioVarioRef = useRef<number>(0)

  const initAudio = () => {
    if (audioCtxRef.current) return
    try {
      const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext
      const ctx = new Ctx()
      const p = PROFILES[varioProfileRef.current]
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = p.waveform
      osc.frequency.value = p.climbBaseFreq
      // LowPass-Filter — bei Profilen ohne lowPassHz quasi-Bypass (hoch genug)
      const lp = ctx.createBiquadFilter()
      lp.type = 'lowpass'
      lp.frequency.value = p.lowPassHz ?? 18000
      lp.Q.value = 0.7
      gain.gain.value = 0
      osc.connect(lp)
      lp.connect(gain)
      gain.connect(ctx.destination)
      osc.start()
      audioCtxRef.current = ctx
      oscRef.current = osc
      gainRef.current = gain
      filterRef.current = lp
    } catch { /* AudioContext blocked oder nicht supported */ }
  }

  // Vario-Profile (1:1 aus FGL-Coaching-App VarioToneGenerator.kt)
  type VarioProfile = {
    name: string
    climbBaseFreq: number; climbMaxFreq: number; climbFreqScale: number
    beepOnMin: number; beepOnMax: number; beepOffMin: number; beepOffMax: number
    sinkBaseFreq: number; sinkMinFreq: number; sinkFreqScale: number
    climbThreshold: number; sinkThreshold: number
    fadeS: number
    useLogFreq: boolean
    waveform: 'sine' | 'triangle'
    lowPassHz: number | null // null = ohne LowPass
  }
  const PROFILES: Record<'WINTER' | 'LX' | 'PESCHGES' | 'VOLKSLOGGER', VarioProfile> = {
    WINTER: {
      name: 'Vario 1', // mechanisch-weich
      climbBaseFreq: 440, climbMaxFreq: 1100, climbFreqScale: 150,
      beepOnMin: 120, beepOnMax: 500, beepOffMin: 100, beepOffMax: 400,
      sinkBaseFreq: 300, sinkMinFreq: 100, sinkFreqScale: 60,
      climbThreshold: 0.2, sinkThreshold: -0.5,
      fadeS: 0.015, useLogFreq: false, waveform: 'sine', lowPassHz: 1500,
    },
    LX: {
      name: 'Vario 2', // digital-klar
      climbBaseFreq: 600, climbMaxFreq: 1800, climbFreqScale: 240,
      beepOnMin: 70, beepOnMax: 350, beepOffMin: 50, beepOffMax: 250,
      sinkBaseFreq: 400, sinkMinFreq: 150, sinkFreqScale: 80,
      climbThreshold: 0.2, sinkThreshold: -0.5,
      fadeS: 0.008, useLogFreq: true, waveform: 'sine', lowPassHz: null,
    },
    PESCHGES: {
      name: 'Vario 3', // Triangle-Wellenform
      climbBaseFreq: 480, climbMaxFreq: 1400, climbFreqScale: 190,
      beepOnMin: 90, beepOnMax: 380, beepOffMin: 90, beepOffMax: 300,
      sinkBaseFreq: 320, sinkMinFreq: 100, sinkFreqScale: 75,
      climbThreshold: 0.2, sinkThreshold: -0.5,
      fadeS: 0.010, useLogFreq: false, waveform: 'triangle', lowPassHz: null,
    },
    VOLKSLOGGER: {
      name: 'Vario 4', // ROUNDED-Charakter via LowPass
      climbBaseFreq: 450, climbMaxFreq: 1300, climbFreqScale: 170,
      beepOnMin: 100, beepOnMax: 450, beepOffMin: 80, beepOffMax: 350,
      sinkBaseFreq: 330, sinkMinFreq: 110, sinkFreqScale: 65,
      climbThreshold: 0.2, sinkThreshold: -0.5,
      fadeS: 0.012, useLogFreq: false, waveform: 'sine', lowPassHz: 1800,
    },
  }
  type VarioProfileKey = 'WINTER' | 'LX' | 'PESCHGES' | 'VOLKSLOGGER'
  const [varioProfile, setVarioProfile] = useState<VarioProfileKey>(() => {
    // R106.4 Stephan-Spec 2026-06-08: Default = PESCHGES (Vario 3)
    if (typeof window === 'undefined') return 'PESCHGES'
    const stored = localStorage.getItem('fgl_vario_profile')
    if (stored === 'LX' || stored === 'PESCHGES' || stored === 'WINTER' || stored === 'VOLKSLOGGER') return stored
    return 'PESCHGES'
  })
  const varioProfileRef = useRef(varioProfile)
  useEffect(() => {
    varioProfileRef.current = varioProfile
    if (typeof window !== 'undefined') {
      localStorage.setItem('fgl_vario_profile', varioProfile)
    }
    // Oscillator-Typ + LowPass-Filter umstellen bei Profil-Wechsel
    const osc = oscRef.current
    const ctx = audioCtxRef.current
    if (osc && ctx) {
      const p = PROFILES[varioProfile]
      osc.type = p.waveform
    }
    const lp = filterRef.current
    if (lp && ctx) {
      const p = PROFILES[varioProfile]
      lp.frequency.setTargetAtTime(p.lowPassHz ?? 18000, ctx.currentTime, 0.02)
    }
  }, [varioProfile])
  const filterRef = useRef<BiquadFilterNode | null>(null)

  const tickAudio = () => {
    const ctx = audioCtxRef.current
    const osc = oscRef.current
    const gain = gainRef.current
    if (!ctx || !osc || !gain) return
    // R106.4: vario direkt aus Schiebeweg (audioVarioRef), -5..+5 m/s linear.
    // NICHT aus autoSpeedRef — der ist fuer Scroll-Geschwindigkeit (-9..+9).
    const vario = audioVarioRef.current
    const P = PROFILES[varioProfileRef.current] // aktuelles Profil
    const t = ctx.currentTime
    const nowMs = performance.now()

    // Sound-Toggle (Closure-safe via Ref): wenn aus, sofort stille.
    if (!soundOnRef.current) {
      gain.gain.setTargetAtTime(0, t, P.fadeS)
      audioRafRef.current = requestAnimationFrame(tickAudio)
      return
    }

    if (vario >= P.climbThreshold) {
      // === STEIGEN: gepulster Piepton, log/linear je nach Profil ===
      const climbRate = vario - P.climbThreshold
      const ratio = Math.min(1, Math.max(0, climbRate / 5))
      let freq: number
      if (P.useLogFreq) {
        // Log-Frequenz wie LX: ln(1 + ratio*9) / ln(10) → 0..1 logarithmisch
        const logRatio = Math.log(1 + ratio * 9) / Math.log(10)
        freq = P.climbBaseFreq + logRatio * (P.climbMaxFreq - P.climbBaseFreq)
      } else {
        freq = Math.min(P.climbMaxFreq, P.climbBaseFreq + climbRate * P.climbFreqScale)
      }
      osc.frequency.setTargetAtTime(freq, t, 0.02)
      // Piep-Timing: kuerzer bei mehr Steigen
      const beepOn = P.beepOnMax - ratio * (P.beepOnMax - P.beepOnMin)
      const beepOff = P.beepOffMax - ratio * (P.beepOffMax - P.beepOffMin)
      // State-Machine
      const bs = beepStateRef.current
      if (nowMs >= bs.switchAtMs) {
        bs.inBeep = !bs.inBeep
        bs.switchAtMs = nowMs + (bs.inBeep ? beepOn : beepOff)
      }
      const targetGain = bs.inBeep ? 0.18 : 0
      gain.gain.setTargetAtTime(targetGain, t, P.fadeS)
    } else if (vario <= P.sinkThreshold) {
      // === SINKEN: durchgehender tiefer Ton, log/linear je nach Profil ===
      const sinkRate = Math.abs(vario) - Math.abs(P.sinkThreshold)
      const ratio = Math.min(1, Math.max(0, sinkRate / 5))
      let freq: number
      if (P.useLogFreq) {
        const logRatio = Math.log(1 + ratio * 9) / Math.log(10)
        freq = P.sinkBaseFreq - logRatio * (P.sinkBaseFreq - P.sinkMinFreq)
      } else {
        freq = Math.max(P.sinkMinFreq, P.sinkBaseFreq - sinkRate * P.sinkFreqScale)
      }
      osc.frequency.setTargetAtTime(freq, t, 0.04)
      gain.gain.setTargetAtTime(0.1, t, P.fadeS) // continuous, leiser als climb
      // Beep-State zuruecksetzen damit naechstes Steigen sofort piept
      beepStateRef.current.inBeep = false
      beepStateRef.current.switchAtMs = 0
    } else {
      // === NULL-ZONE: Totband, stille ===
      gain.gain.setTargetAtTime(0, t, P.fadeS)
      beepStateRef.current.inBeep = false
      beepStateRef.current.switchAtMs = 0
    }
    audioRafRef.current = requestAnimationFrame(tickAudio)
  }

  const stopAudio = () => {
    const ctx = audioCtxRef.current
    const gain = gainRef.current
    if (audioRafRef.current !== null) {
      cancelAnimationFrame(audioRafRef.current)
      audioRafRef.current = null
    }
    if (ctx && gain) {
      const P = PROFILES[varioProfileRef.current]
      gain.gain.setTargetAtTime(0, ctx.currentTime, P.fadeS)
    }
    beepStateRef.current = { inBeep: false, switchAtMs: 0 }
  }

  useEffect(() => () => {
    if (audioRafRef.current !== null) cancelAnimationFrame(audioRafRef.current)
    audioCtxRef.current?.close().catch(() => {})
  }, [])

  const setThrottle = (clientY: number) => {
    const track = throttleTrackRef.current
    if (!track) return
    const rect = track.getBoundingClientRect()
    const raw = ((clientY - rect.top) / rect.height) * 100
    const clamped = Math.max(0, Math.min(100, raw))
    setThrottlePos(clamped)
    // Speed-Mapping fuer Scroll: 50 = 0, 0 = max-up (-9), 100 = max-down (+9)
    const offset = clamped - 50
    autoSpeedRef.current = (offset / 50) * 9
    // R106.4: Vario-Mapping fuer Sound — UNABHAENGIG vom Scroll-Speed.
    // Voller Schiebeweg = vollen Vario-Bereich -5..+5 m/s (linear).
    // pos 0% (oben) → vario = +5 m/s (max Steigton)
    // pos 50% (Mitte) → vario = 0 (Totband, stille)
    // pos 100% (unten) → vario = -5 m/s (max Sinkton)
    audioVarioRef.current = -(offset / 50) * 5
  }
  // R106.4 (Stephan-Spec 2026-06-08): Pointer-Events fangen Mouse + Touch
  // gleichermassen. Sicher fuer Desktop und Mobile.
  const onThrottleDown = (e: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    let y = 0
    if ('clientY' in e) y = (e as React.PointerEvent | React.MouseEvent).clientY
    else if ('touches' in e && e.touches.length > 0) y = e.touches[0].clientY
    setThrottleActive(true)
    setThrottle(y)
    initAudio()
    audioCtxRef.current?.resume().catch(() => {})
    if (audioRafRef.current === null) {
      audioRafRef.current = requestAnimationFrame(tickAudio)
    }
  }
  useEffect(() => {
    if (!throttleActive) return
    const move = (e: MouseEvent) => setThrottle(e.clientY)
    const touchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return
      e.preventDefault() // verhindert Page-Scroll waehrend Drag
      setThrottle(e.touches[0].clientY)
    }
    const up = () => {
      setThrottleActive(false)
      setThrottlePos(50)
      autoSpeedRef.current = 0
      audioVarioRef.current = 0
      stopAudio()
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    window.addEventListener('touchmove', touchMove, { passive: false })
    window.addEventListener('touchend', up)
    window.addEventListener('touchcancel', up)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      window.removeEventListener('touchmove', touchMove)
      window.removeEventListener('touchend', up)
      window.removeEventListener('touchcancel', up)
    }
  }, [throttleActive])

  useEffect(() => {
    let cancelled = false
    function load() {
      // R103.35 (Stephan-Spec): kein Cap mehr — alle Fluege von heute holen,
      // Scroll uebernimmt der Body. limit hochgezogen, API hat Hard-Cap 500.
      fetch('/api/public/ticker/recent-flights?todayOnly=1&limit=500')
        .then(r => r.ok ? r.json() : { data: [], total: 0 })
        .then(j => {
          if (cancelled) return
          if (j?.data) setItems(j.data)
          if (typeof j?.total === 'number') setTotal(j.total)
          setLoaded(true)
        })
        .catch(() => { if (!cancelled) setLoaded(true) })
    }
    load()
    const t = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [])

  if (!loaded) return null

  // R103.34 (Stephan-Spec): Card zeigt Eintraege, Rest scrollt.
  // r19.55.86 (Stephan-Spec 2026-08-05): maxHeight jetzt DYNAMISCH per
  // getBoundingClientRect gemessen — Sidebar-Unterkante sitzt pixel-genau
  // auf der Bottom-Edge der View-Tab-Pillen (BESTER EINZELFLUG / KLASSEN /
  // ZEITWERTUNG). Fallback 260 px, siehe useEffect oben. Responsive via
  // ResizeObserver.
  // R106.4.6 Mobile-Akkordeon: Wenn collapsed, schrumpft Card auf Header-Hoehe.
  const isMobileAccordion = hydrated && mobileCollapsed
  return (
    <div
      ref={cardRef}
      className="today-col-wrap rounded-2xl flex flex-col relative"
      style={{
        // R103.53: theme-aware via CSS-Vars + Glas-Effekt
        background: 'color-mix(in oklch, var(--bg-card) 75%, transparent)',
        backdropFilter: 'blur(22px) saturate(180%)',
        WebkitBackdropFilter: 'blur(22px) saturate(180%)',
        boxShadow: [
          '0 8px 28px color-mix(in oklch, var(--bg) 70%, transparent)',
          'inset 0 0 0 1px color-mix(in oklch, var(--accent) 28%, transparent)',
          '0 0 16px color-mix(in oklch, var(--accent) 25%, transparent)',
        ].join(', '),
        maxHeight: dynamicMaxHeight,
        border: '1.5px solid transparent',
        backgroundClip: 'padding-box',
      }}
    >
      {/* Animated Gold-Border-Ring (via Pseudo-Element CSS) */}
      <style jsx>{`
        .today-col-wrap::before {
          content: '';
          position: absolute;
          inset: -1.5px;
          border-radius: 16px;
          padding: 1.5px;
          background: linear-gradient(
            135deg,
            rgba(242,201,78,0.7) 0%,
            rgba(242,201,78,0.25) 40%,
            rgba(242,201,78,0.7) 70%,
            rgba(242,201,78,0.3) 100%
          );
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
          z-index: 0;
        }
      `}</style>
      {/* Header — auf Mobile zugleich Akkordeon-Toggle */}
      <div
        className="px-3 py-2.5 border-b relative flex items-start justify-between gap-2"
        style={{
          borderColor: isMobileAccordion
            ? 'transparent'
            : 'color-mix(in oklch, var(--accent) 28%, transparent)',
          zIndex: 10,
        }}
      >
        <button
          type="button"
          onClick={toggleMobile}
          className="md:cursor-default md:pointer-events-none flex-1 min-w-0 flex items-center gap-2 text-left bg-transparent border-0 p-0 m-0"
          aria-expanded={!mobileCollapsed}
          aria-label={mobileCollapsed ? t('today.expandListAria') : t('today.collapseListAria')}
          data-tip={mobileCollapsed ? t('today.expandListTip') : t('today.collapseListTip')}
        >
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] font-black uppercase tracking-[0.16em]"
              style={{ color: 'var(--accent)' }}
            >
              {t('today.last48hTitle')}
            </div>
            <div
              className="text-[10px] font-medium mt-0.5 tabular-nums"
              style={{ color: 'var(--text-muted, var(--text))', opacity: 0.78 }}
            >
              {total === 1 ? t('today.flightsCountOne') : t('today.flightsCountMany', { count: total })}
            </div>
          </div>
          {/* R106.4.6: Chevron NUR auf Mobile sichtbar */}
          <span
            className="md:hidden shrink-0 inline-flex items-center justify-center rounded-full"
            style={{
              width: 24, height: 24,
              background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)',
              color: 'var(--accent)',
            }}
            aria-hidden
          >
            {mobileCollapsed
              ? <ChevronDown className="w-3.5 h-3.5" />
              : <ChevronUp className="w-3.5 h-3.5" />}
          </span>
        </button>
        {/* R106.4 (Stephan-Spec 2026-06-08): Vario-Profile-Dropdown + Toggle.
            position: relative + zIndex: 11 = stellt sicher dass die Controls
            ueber dem Glider-Backdrop + Hit-Area des Thermikhebels klickbar sind.
            R106.4.6 (Mobile-Akkordeon): auf Mobile-collapsed verstecken. */}
        <div
          className={`${isMobileAccordion ? 'hidden' : 'flex'} md:flex shrink-0 items-center gap-1.5`}
          style={{ position: 'relative', zIndex: 11 }}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          <select
            value={varioProfile}
            onChange={e => setVarioProfile(e.target.value as VarioProfileKey)}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            className="text-[10px] font-black uppercase tracking-wider rounded-full cursor-pointer"
            style={{
              background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)',
              color: 'var(--accent)',
              padding: '4px 8px',
              minHeight: 28,
              position: 'relative',
              zIndex: 12,
              pointerEvents: 'auto',
            }}
            data-tip={t('today.varioProfileTip')}
            aria-label={t('today.varioProfileAria')}
          >
            {(Object.keys(PROFILES) as VarioProfileKey[]).map(k => (
              <option key={k} value={k}>{PROFILES[k].name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setSoundOn(v => !v) }}
            onMouseDown={e => e.stopPropagation()}
            className="inline-flex items-center justify-center rounded-full transition-colors cursor-pointer"
            style={{
              width: 30, height: 30,
              background: soundOn ? 'color-mix(in oklch, var(--accent) 18%, transparent)' : 'transparent',
              border: `1px solid color-mix(in oklch, var(--accent) ${soundOn ? 36 : 18}%, transparent)`,
              color: soundOn ? 'var(--accent)' : 'color-mix(in oklch, var(--accent) 60%, var(--text))',
              position: 'relative',
              zIndex: 12,
              pointerEvents: 'auto',
            }}
            data-tip={soundOn ? t('today.soundOnTip') : t('today.soundOffTip')}
            aria-label={soundOn ? t('today.soundOffAria') : t('today.soundOnAria')}
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Body — R106.4.6 (Mobile-Akkordeon): auf Mobile-collapsed komplett verstecken */}
      {items.length === 0 ? (
        <div
          className={`${isMobileAccordion ? 'hidden md:block' : ''} px-3 py-5 text-[11px] italic`}
          style={{ color: 'var(--text-muted, var(--text))', opacity: 0.7 }}
        >
          In den letzten 48 Stunden nichts eingereicht.
        </div>
      ) : (
        <div className={`${isMobileAccordion ? 'hidden md:flex' : 'flex'} flex-1 min-h-0 relative z-[1]`}>
        <div
          ref={scrollRef}
          className="today-col-scroll flex flex-col overflow-y-auto overscroll-contain flex-1"
          style={{
            maskImage: 'linear-gradient(to bottom, #000 0%, #000 calc(100% - 28px), transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 calc(100% - 28px), transparent 100%)',
          }}
        >
          {items.map(it => (
            <Link
              key={it.id}
              href={`/flight/${it.id}`}
              className="group flex items-center gap-2 px-3 py-2 border-b last:border-b-0 transition-colors"
              style={{
                borderColor: 'color-mix(in oklch, var(--text) 8%, transparent)',
                // Sicherstellen dass der Link klickbar ist (mask-image kann pointer-events stoeren)
                pointerEvents: 'auto',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'color-mix(in oklch, var(--accent) 8%, transparent)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
              }}
            >
              {/* Avatar 26 px */}
              <span
                className="relative shrink-0 rounded-full overflow-hidden flex items-center justify-center"
                style={{
                  width: 26,
                  height: 26,
                  background: 'color-mix(in oklch, var(--accent) 12%, transparent)',
                  color: 'var(--accent)',
                  fontSize: 10,
                  fontWeight: 900,
                  border: '1px solid color-mix(in oklch, var(--accent) 32%, transparent)',
                }}
              >
                {it.pilot.avatarUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={it.pilot.avatarUrl}
                    alt={it.pilot.name}
                    width={26}
                    height={26}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span>{getInitial(it.pilot.name)}</span>
                )}
              </span>
              {/* Name + Punktzahl */}
              <div className="flex-1 min-w-0">
                <div
                  className="text-[11.5px] font-bold truncate group-hover:underline"
                  style={{ color: 'var(--text)' }}
                  data-tip={it.pilot.name}
                >
                  {it.pilot.name}
                </div>
                <div
                  className="text-[11px] font-black tabular-nums"
                  style={{ color: 'var(--accent)' }}
                >
                  {it.wertungsDistanceKm != null ? `${it.wertungsDistanceKm.toFixed(2)} km` : '—'}
                </div>
              </div>
            </Link>
          ))}
        </div>
        {/* R103.53 Thermikhebel-Slider — Stephan-Wunsch aus Skizze.
            Mitte = neutral, hoch ziehen = scroll-up, runter = scroll-down.
            Speed proportional zur Distanz von Mitte. Loslassen federt zurueck. */}
        <div
          ref={throttleTrackRef}
          onMouseDown={onThrottleDown}
          onTouchStart={onThrottleDown}
          className="relative shrink-0 mx-1 my-2 rounded-full select-none"
          style={{
            width: 14,
            cursor: 'grab',
            background: 'color-mix(in oklch, var(--accent) 18%, transparent)',
            border: '1px solid color-mix(in oklch, var(--accent) 42%, transparent)',
            boxShadow: 'inset 0 0 6px color-mix(in oklch, var(--accent) 24%, transparent)',
            touchAction: 'none', // verhindert Scroll-Gestures auf Mobile
          }}
          data-tip="Thermikhebel — ziehen zum Scrollen, loslassen federt zurück"
        >
          {/* Mittel-Linie (Neutralpunkt) */}
          <div
            className="absolute left-0 right-0 top-1/2"
            style={{
              height: 1,
              background: 'color-mix(in oklch, var(--accent) 38%, transparent)',
              transform: 'translateY(-0.5px)',
            }}
            aria-hidden
          />
          {/* R106.4 (Stephan-Spec 2026-06-08): groessere transparente Hit-Area
              (56×56 px = 200%) um den visuellen Halbkugel-Knopf herum.
              Verbessert Maus-Greifbarkeit + Mobile-Touch-Target. */}
          <div
            onMouseDown={onThrottleDown}
            onTouchStart={onThrottleDown}
            className="absolute left-1/2 flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              top: `${throttlePos}%`,
              transform: 'translate(-50%, -50%)',
              transition: throttleActive ? 'none' : 'top 0.35s cubic-bezier(0.34,1.56,0.64,1)',
              cursor: throttleActive ? 'grabbing' : 'grab',
              touchAction: 'none',
              zIndex: 3,
            }}
            aria-hidden
          >
          {/* R106.3 (Stephan-Spec 2026-06-07): gefuellte Halbkugel als Knopf,
              Tragflaechen ragen seitlich UEBER den Kreis hinaus → man kann
              seitlich an die Tragflaeche greifen und hoch/runter schieben. */}
          <div
            className="rounded-full flex items-center justify-center relative"
            style={{
              width: 28,
              height: 28,
              // 3D-Halbkugel-Look: Highlight oben-links, Schatten unten-rechts
              background: 'radial-gradient(circle at 32% 28%, color-mix(in oklch, white 60%, var(--accent)) 0%, var(--accent) 38%, color-mix(in oklch, var(--accent) 55%, black) 100%)',
              boxShadow: throttleActive
                ? '0 0 16px color-mix(in oklch, var(--accent) 90%, transparent), inset -1px -2px 4px color-mix(in oklch, black 40%, transparent), inset 1px 1px 2px color-mix(in oklch, white 40%, transparent)'
                : '0 3px 8px color-mix(in oklch, black 45%, transparent), inset -1px -2px 4px color-mix(in oklch, black 30%, transparent), inset 1px 1px 2px color-mix(in oklch, white 30%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 55%, transparent)',
              overflow: 'visible',
            }}
            aria-hidden
          >
            {/* R106.3 (Stephan-Spec 2026-06-07): fertige Silhouette aus
                public/brand/glider-v5.png als CSS-Mask — gleiches Asset
                wie GliderOrbit am Hero (.award-glider in globals.css). */}
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 110,
                height: 36,
                transform: 'translate(-50%, -50%)',
                WebkitMaskImage: 'url(/brand/glider-v5.png)',
                maskImage: 'url(/brand/glider-v5.png)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                // contain statt 100% 100% → echtes Glider-Verhaeltnis bleibt
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                backgroundColor: 'var(--glider-color, var(--text, #14202a))',
                filter: 'drop-shadow(0 1px 2px color-mix(in oklch, black 60%, transparent))',
                pointerEvents: 'none',
                zIndex: 2,
              }}
              aria-hidden
            />
          </div>
          </div>
        </div>
        </div>
      )}
    </div>
  )
}
